/**
 * Team-role display map (Phase 1.6).
 *
 * THE SINGLE SOURCE OF TRUTH for snake_case → display string conversion.
 * Consumed by the Team tab role picker, the Recent-activity timeline, the
 * People tab access column, and the Permissions matrix column headers.
 *
 * ---------------------------------------------------------------------
 * COLLAPSED 2026-08-27 — THERE WERE TWO VOCABULARIES, AND THE PICKER
 * RENDERED BOTH
 * ---------------------------------------------------------------------
 * Until this change the product carried two competing names for every
 * role. This map held one set ('Primary CM', 'Admin support'); a second
 * `accessRoleLabel` set in src/lib/people.ts held another ('Clinical
 * Team', 'Office support'), justified as "what the access level MEANS,
 * not the internal role name". Two more copies had drifted out of both:
 * a hand-maintained duplicate in RecentActivityRow.tsx and a third map
 * in permissionCopy.ts that said 'Care manager' where this one said
 * 'Primary CM'.
 *
 * The Team-tab role picker rendered the first two ADJACENT, separated by
 * a middot:
 *
 *     Clinical Team · Primary CM
 *     Supervisor · Supervising CM
 *     Office support · Admin support
 *
 * Two names for one role, on the screen where you assign it. That is not
 * a labelling nit; it is the reason nobody could say what a seat was.
 *
 * Founder ruling 2026-08-27 settles the vocabulary. Note that the chosen
 * labels are largely the ACCESS-label set, not this one — 'Clinical Team'
 * and 'Supervisor' were already in the product and already read better.
 * The names now describe the seat as the practice speaks of it:
 *
 *   - No fake ladder. 'Admin support' and 'Exec support' implied a tier
 *     the capability matrix never implemented (admin_support IS a strict
 *     subset of data_steward, but exec_support is NOT on that ladder —
 *     it is a sideways seat that owns the money).
 *   - No 'administrator' beside 'administrative', which was the specific
 *     collision the founder called out.
 *   - 'Clinical Team' rather than 'Care manager', because the role also
 *     carries SPICE staff (see the does_spice staff flag and the
 *     spice_therapist billable role) — 'Care manager' is too narrow.
 *
 * ROLE_BLURB carries what a label alone cannot: the one-line answer to
 * "what is this seat for?". Two pairs need it — 'Clinical Supervisor'
 * and 'Clinical Team' are nested (a supervisor is on the clinical team;
 * the distinguishing fact is CASELOAD SCOPE, not clinical-ness), and
 * 'MSO Staff' no longer says "Read-only" in its label, so the blurb says
 * it instead.
 *
 * §m: this constant has no server-side duplicate; the server uses the
 * snake_case enum directly. Display is a frontend concern. The ROLE KEYS
 * are deliberately untouched — they live in a CHECK constraint on
 * user_tenants.role, in RLS policies across ~15 migrations, and in
 * role_capability_overrides rows. Renaming a key is a migration that must
 * remap those rows in the same transaction or permissions silently stop
 * applying. This change is copy only.
 */

import type { UserTenantRole } from './types';

export const ROLE_DISPLAY: Record<UserTenantRole, string> = {
  owner:           'Owner',
  // Nicole. Wider than client services: she also runs HR and holds the
  // provider-directory authority (approve / merge_providers /
  // manage_lookups). The role names the widest hat, not the business card.
  data_steward:    'Operations',
  // Laila. The money seat — rates, the whole invoice chain, the QuickBooks
  // customer links — plus process and checklist authoring. Nobody else
  // holds issue/push/link, the owner included.
  exec_support:    'Business Manager',
  supervising_cm:  'Clinical Supervisor',
  // Not 'Care manager': the role also carries SPICE staff.
  primary_cm:      'Clinical Team',
  // Marcia.
  admin_support:   'Administrative Support',
  // mso_staff is set via service_engagements, never via Team tab.
  // Included so the map is total over the role union; should never
  // surface as a Team-tab affordance label.
  mso_staff:       'MSO Staff',
  // Board observer (0152) — grantable from the Team tab (read-only board seat).
  board_observer:  'Board Observer',
  // The Directory intern: edits the directory, no client records by default.
  directory_editor: 'Directory Editor',
  accounting:      'Accounting',
};

/**
 * One line per role: what the seat is FOR. The Permissions matrix already
 * gives every capability a blurb; roles carried only a label and a head
 * count, which is exactly where "what is this seat?" went unanswered.
 */
export const ROLE_BLURB: Record<UserTenantRole, string> = {
  owner:           'Everything, including QuickBooks and voids.',
  data_steward:    'Client services, HR, the directory.',
  exec_support:    'Billing, invoicing, office processes.',
  supervising_cm:  'Oversees CMs; sees all clients.',
  primary_cm:      'Their own caseload.',
  admin_support:   'Day-to-day office support.',
  mso_staff:       'Read-only, via service engagements.',
  board_observer:  'Reads all; writes only the demo client.',
  directory_editor: 'Edits the directory. No client records by default.',
  accounting:      'Bookkeeping and accounting: billing, payroll hours and reports. No client records.',
};

/**
 * Roles selectable in the Team tab (mso_staff excluded — onboarded via
 * service_engagements per ADR-006). Mirrors api/_shared/teamBulkAdd/
 * parser.ts TEAM_BULK_ADD_GRANTABLE_ROLES.
 */
export const TEAM_GRANTABLE_ROLES: ReadonlyArray<UserTenantRole> = [
  'owner',
  'data_steward',
  // X.5.4 — exec_support grantable from the Team tab (mirrors the server
  // TEAM_BULK_ADD_GRANTABLE_ROLES; DB CHECK already permits it via 0083).
  'exec_support',
  'supervising_cm',
  'primary_cm',
  'admin_support',
  // The Directory Editor: added as Admin, edits the directory, no client
  // records by default.
  'directory_editor',
  // Board observer (0152) — grantable read-only board seat. DB CHECK permits
  // it via migration 0152 (pending_grants_role_check widened).
  'board_observer',
  // 0220: the accounting seat (bookkeepers and accountants).
  'accounting',
];

export function displayRole(role: string | null | undefined): string {
  if (!role) return 'unknown';
  if (role in ROLE_DISPLAY) return ROLE_DISPLAY[role as UserTenantRole];
  return role; // unrecognized — fall through to raw
}

/** Blurb for a role, or empty string for an unrecognized one. */
export function roleBlurb(role: string | null | undefined): string {
  if (!role) return '';
  if (role in ROLE_BLURB) return ROLE_BLURB[role as UserTenantRole];
  return '';
}
